import {
  type RestFeatureComparisonCell,
  type RestFeatureComparisonCellStatus,
  type RestFeatureComparisonColumnCount,
  type RestFeatureComparisonLaCard,
  type RestFeatureComparisonRow,
} from '@bbkAdminUtils/api-client/rest-la-cards-types';

type FeatureComparisonRequires = RestFeatureComparisonLaCard['requires'];

/** Default icon-pack ids for status cells (ExitSurveyIcons). */
export const FEATURE_COMPARISON_DEFAULT_POSITIVE_ICON = 'bbk-esi-check';

export const FEATURE_COMPARISON_DEFAULT_NEGATIVE_ICON = 'bbk-esi-block';

export const FEATURE_COMPARISON_NEW_ROW_LABEL = 'New feature';

/** First column is included, the rest are not. Matches the starter rows. */
const defaultStatusForColumn = (
  columnIndex: number
): RestFeatureComparisonCellStatus =>
  columnIndex === 0 ? 'positive' : 'negative';

export const createFeatureComparisonRow = (
  columnCount: RestFeatureComparisonColumnCount,
  label = FEATURE_COMPARISON_NEW_ROW_LABEL
): RestFeatureComparisonRow => ({
  label,
  cells: Array.from({ length: columnCount }, (_unused, i) => ({
    status: defaultStatusForColumn(i),
  })),
});

export const FEATURE_COMPARISON_DEFAULT_ROWS: RestFeatureComparisonRow[] = [
  createFeatureComparisonRow(2, 'Personalized cancel experiences'),
  createFeatureComparisonRow(
    2,
    'Save offers (discounts, pauses, plan changes)'
  ),
  createFeatureComparisonRow(2, 'Audience targeting & plays'),
];

/** Table defaults shared by the layout picker sample and the new-offer template. */
export const FEATURE_COMPARISON_DEFAULT_TABLE = {
  feature_column_header: 'Capabilities',
  comparison_column_count: 2,
  comparison_columns: [{ header: 'With Growth' }, { header: 'Without Growth' }],
  positive_icon_picker: FEATURE_COMPARISON_DEFAULT_POSITIVE_ICON,
  negative_icon_picker: FEATURE_COMPARISON_DEFAULT_NEGATIVE_ICON,
  rows: FEATURE_COMPARISON_DEFAULT_ROWS,
} satisfies Partial<FeatureComparisonRequires>;

/** Normalize 2 | 3 from select values that may arrive as numbers or strings. */
export const resolveFeatureComparisonColumnCount = (
  count: unknown,
  fallbackColumnsLength = 0
): RestFeatureComparisonColumnCount => {
  const raw =
    count === undefined || count === null ? fallbackColumnsLength : count;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return n >= 3 ? 3 : 2;
};

export const getFeatureComparisonColumnCount = (
  requires:
    | Pick<
        FeatureComparisonRequires,
        'comparison_column_count' | 'comparison_columns'
      >
    | undefined
): RestFeatureComparisonColumnCount =>
  resolveFeatureComparisonColumnCount(
    requires?.comparison_column_count,
    requires?.comparison_columns?.length ?? 0
  );

/** Cells missing from saved data render as not included. */
export const getFeatureComparisonCell = (
  cells: RestFeatureComparisonCell[] | undefined,
  columnIndex: number
): RestFeatureComparisonCell => cells?.[columnIndex] ?? { status: 'negative' };

type ResizableRequires = Partial<
  Pick<
    FeatureComparisonRequires,
    'comparison_column_count' | 'comparison_columns' | 'rows'
  >
>;

type FeatureComparisonTable = Pick<
  FeatureComparisonRequires,
  | 'feature_column_header'
  | 'comparison_column_count'
  | 'comparison_columns'
  | 'rows'
>;

/** Fill sparse saved table data before the editor renders or updates it. */
export const normalizeFeatureComparisonTable = <
  T extends Partial<FeatureComparisonTable>,
>(
  table: T
): T & FeatureComparisonTable => {
  const columnCount = getFeatureComparisonColumnCount({
    comparison_column_count: table.comparison_column_count,
    comparison_columns: table.comparison_columns ?? [],
  });
  const comparisonColumns = [...(table.comparison_columns ?? [])];
  while (comparisonColumns.length < columnCount) {
    comparisonColumns.push({
      header: `Column ${comparisonColumns.length + 1}`,
    });
  }
  comparisonColumns.length = columnCount;

  const rows = (table.rows ?? []).map((row) => {
    const cells = [...(row.cells ?? [])];
    while (cells.length < columnCount) {
      cells.push({ status: 'negative' });
    }
    cells.length = columnCount;
    return { ...row, cells };
  });

  return {
    ...table,
    feature_column_header: table.feature_column_header ?? '',
    comparison_column_count: columnCount,
    comparison_columns: comparisonColumns,
    rows,
  } as T & FeatureComparisonTable;
};

/**
 * Keep comparison_columns and each row's cells aligned with the column count.
 * Growing pads with default headers and negative cells. Shrinking drops
 * trailing entries.
 */
export const resizeFeatureComparisonRequires = <T extends ResizableRequires>(
  requires: T,
  nextCount: RestFeatureComparisonColumnCount
): T => {
  const columns = [...(requires.comparison_columns ?? [])];
  while (columns.length < nextCount) {
    columns.push({ header: `Column ${columns.length + 1}` });
  }
  columns.length = nextCount;

  const rows = (requires.rows ?? []).map((row) => {
    const cells = [...(row.cells ?? [])];
    while (cells.length < nextCount) {
      cells.push({ status: 'negative' });
    }
    cells.length = nextCount;
    return { ...row, cells };
  });

  return {
    ...requires,
    comparison_column_count: nextCount,
    comparison_columns: columns,
    rows,
  };
};
